import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

import { Icon, type IconName } from './Icon'
import { Spinner } from './Spinner'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-strong',
  secondary: 'border border-border bg-surface text-fg hover:bg-surface-2',
  // Цвет текста ghost — только если вызывающий не задал свой (text-danger, text-accent…): cn не сливает классы.
  ghost: 'hover:bg-surface-2',
  danger: 'bg-danger text-danger-fg hover:opacity-90',
}

const TEXT_COLOR = /(^|\s)text-(fg|muted|accent|danger|warning)(\s|$)/

// На телефоне цели нажатия не меньше 44 px; на десктопе sm/md компактнее.
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-11 gap-1.5 px-3 text-sm lg:min-h-8',
  md: 'min-h-11 gap-2 px-4 text-sm lg:min-h-10',
  lg: 'min-h-14 gap-2 px-5 text-base',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: IconName
  block?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    block,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex select-none items-center justify-center rounded-lg font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        variant === 'ghost' && !TEXT_COLOR.test(className ?? '') && 'text-fg',
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon ? <Icon name={icon} size={18} /> : null}
      {children}
    </button>
  )
})

/** Кнопка только с иконкой; подпись обязательна (aria-label). */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'icon' | 'children'> & { icon: IconName; label: string }
>(function IconButton({ icon, label, className, variant = 'ghost', ...rest }, ref) {
  return (
    <Button
      ref={ref}
      variant={variant}
      aria-label={label}
      title={label}
      // px-0! — иначе px-4 из SIZES перебивает (cn не сливает классы) и кнопка шире 44 px.
      className={cn('min-w-11 px-0! lg:min-w-9', className)}
      {...rest}
    >
      <Icon name={icon} size={20} />
    </Button>
  )
})
